import React, { useState } from "react";
import api from "../api";
import toast from "react-hot-toast";
import { Building2, ArrowRight, ShieldCheck, X } from "lucide-react";

/**
 * Company-domain verification (Pro/Max guard).
 * Two steps: request a code to a company mailbox, then enter it.
 * Props: open, onClose(), onVerified()  (onVerified fires after a successful verify)
 */
export default function CompanyVerifyModal({ open, onClose, onVerified }) {
  const [step, setStep] = useState("request");
  const [companyEmail, setCompanyEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => { setStep("request"); setCompanyEmail(""); setCode(""); setBusy(false); };
  const close = () => { reset(); onClose && onClose(); };

  const sendCode = async () => {
    if (!companyEmail.trim()) { toast.error("Enter your company email"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/company/request", { company_email: companyEmail.trim() });
      toast.success("Code sent to " + companyEmail.trim());
      setStep("verify");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter the code"); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/company/verify", { company_email: companyEmail.trim(), code: code.trim() });
      toast.success("Company domain verified ✓");
      reset();
      onVerified && onVerified();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="au-glass" style={S.modal}>
        <div style={S.head}>
          <Building2 size={20} style={{ color: "var(--au-accent-ink)" }} />
          <span style={{ fontSize: 17, fontWeight: 700, color: "var(--au-text)" }}>Company Verification Required</span>
          <button onClick={close} style={S.x}><X size={16} /></button>
        </div>

        <p style={{ color: "var(--au-muted)", fontSize: 13, lineHeight: 1.7, marginBottom: 18 }}>
          Your plan requires a verified company domain before scanning. Enter a company email address
          (not a personal Gmail/Outlook) — we'll send a one-time code there to confirm you're authorized.
        </p>

        {step === "request" ? (
          <>
            <label style={S.label}>COMPANY EMAIL</label>
            <input
              className="au-input au-mono" style={{ width: "100%" }}
              placeholder="you@yourcompany.com"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && sendCode()}
              disabled={busy}
            />
            <div style={S.actions}>
              <button onClick={close} className="au-pill au-pill-glass au-pill-sm">Cancel</button>
              <button onClick={sendCode} disabled={busy} className="au-pill au-pill-primary au-pill-sm">
                {busy ? "Sending…" : <>Send code <ArrowRight size={14} /></>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: "var(--au-text-2)", marginBottom: 12 }}>
              Code sent to <strong className="au-mono" style={{ color: "var(--au-accent-ink)" }}>{companyEmail}</strong>
            </div>
            <label style={S.label}>VERIFICATION CODE</label>
            <input
              className="au-input au-mono" style={{ width: "100%", letterSpacing: 6, fontSize: 18, textAlign: "center" }}
              placeholder="000000" maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && verify()}
              disabled={busy}
            />
            <div style={S.actions}>
              <button onClick={() => setStep("request")} className="au-pill au-pill-glass au-pill-sm" disabled={busy}>Back</button>
              <button onClick={verify} disabled={busy} className="au-pill au-pill-primary au-pill-sm">
                {busy ? "Verifying…" : <>Verify <ShieldCheck size={14} /></>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { padding: 28, maxWidth: 460, width: "100%", display: "flex", flexDirection: "column" },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid var(--au-hair)" },
  x: { marginLeft: "auto", background: "transparent", border: "none", color: "var(--au-muted)", cursor: "pointer", display: "inline-flex" },
  label: { display: "block", fontSize: 11, color: "var(--au-muted)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 },
};
